import React, { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { connectPublicMatrix } from "../lib/socket";
import { api } from "../lib/api";
import { BANDS, MODES, isBandModeBlocked, formatFrequencyDisplay, flagImgUrl, colors, fontMono, formatUtcTime, s } from "../lib/theme";

const MAX_RECENT = 20;

export default function PublicMatrix() {
  const { activityId } = useParams();
  const [activity, setActivity] = useState(null);
  const [occupancy, setOccupancy] = useState([]);
  const [recentQsos, setRecentQsos] = useState([]);
  const [entities, setEntities] = useState([]);
  const [notFound, setNotFound] = useState(false);
  const socketRef = useRef(null);

  useEffect(() => {
    // Estado inicial por REST (incluye nombre/logo/bandas/modos de la
    // actividad), y luego se mantiene "en directo" por WebSocket — siempre
    // acotado a esta actividad (activityId), nunca se mezcla con otras.
    api.publicMatrix(activityId)
      .then((data) => {
        setActivity(data.activity);
        setOccupancy(data.occupancy || []);
      })
      .catch(() => setNotFound(true));

    api.publicRecentQsos(activityId).then(setRecentQsos).catch(() => {});
    api.publicCountries(activityId).then((data) => {
      setEntities(data.entities || []);
    }).catch(() => {});

    const socket = connectPublicMatrix();
    socketRef.current = socket;
    socket.on("connect", () => {
      socket.emit("join_activity", { activityId }, (rows) => setOccupancy(rows || []));
    });
    socket.on("matrix_update", (rows) => setOccupancy(rows));
    socket.on("qso-added", (qso) => {
      setRecentQsos((prev) => [qso, ...prev].slice(0, MAX_RECENT));
      setActivity((prev) => (prev ? { ...prev, qso_count: (prev.qso_count || 0) + 1 } : prev));
      if (qso.entity) {
        setEntities((prev) => {
          const existing = prev.find((e) => e.entity === qso.entity);
          if (existing) {
            return prev
              .map((e) => (e.entity === qso.entity ? { ...e, count: e.count + 1 } : e))
              .sort((a, b) => b.count - a.count);
          }
          return [...prev, { entity: qso.entity, isoCode: qso.entityIsoCode, flag: qso.entityFlag, count: 1 }].sort((a, b) => b.count - a.count);
        });
      }
    });
    return () => socket.disconnect();
  }, [activityId]);

  const cellFor = (band, mode) => occupancy.find((o) => o.band === band && o.mode === mode) || null;

  // Solo se muestran las bandas/modos configurados para esta actividad
  // concreta — nunca el catálogo completo de la app.
  const activeBands = activity ? BANDS.filter((b) => (activity.bands || []).includes(b.id)) : [];
  const activeModes = activity ? MODES.filter((m) => (activity.modes || []).includes(m)) : [];

  if (notFound) {
    return (
      <div style={{ ...s.page, maxWidth: 1680 }}>
        <div style={s.errorBox}>Esta actividad no existe o no está en directo ahora mismo.</div>
        <Link to="/directo" style={{ color: colors.amber }}>← Todas las actividades</Link>
      </div>
    );
  }

  return (
    <div style={{ ...s.page, maxWidth: 1680 }}>
      <Link to="/directo" style={{ color: colors.textDim, fontSize: 14, fontWeight: 700, textDecoration: "none" }}>← Todas las actividades</Link>

      <div style={{ display: "flex", alignItems: "center", gap: 6, margin: "14px 0 4px" }}>
        <span style={{ width: 7, height: 7, borderRadius: "50%", background: activity?.finished ? "#ef4444" : colors.green, display: "inline-block" }} />
        <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", color: activity?.finished ? "#ef4444" : colors.green }}>
          {activity?.finished ? "FINALIZADO" : "EN DIRECTO"}
        </span>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        {activity?.logo_url && (
          <img src={activity.logo_url} alt="" style={{ width: 32, height: 32, borderRadius: 6, objectFit: "cover", border: `1px solid ${colors.lineStrong}` }} />
        )}
        <h1 style={s.h1}>{activity?.name || "Actividad en directo"}</h1>
        {activity?.callsign && (
          <span style={{
            fontFamily: fontMono, fontSize: 14, fontWeight: 700, color: colors.amber,
            background: colors.panelRaised, border: `1px solid ${colors.lineStrong}`, borderRadius: 6, padding: "3px 9px",
          }}>
            {activity.callsign}
          </span>
        )}
      </div>
      {activity?.finished && (
        <p style={{ fontSize: 13, color: "#ffb4b4", marginTop: 6 }}>
          Esta actividad ya ha finalizado — se muestra en modo solo lectura.
        </p>
      )}
      <p style={s.subtitle}>Vista pública, sin necesidad de iniciar sesión. Todas las horas en UTC.</p>
      {activity && (
        <p style={{ fontSize: 13, color: colors.textDim, marginTop: 4 }}>
          <b style={{ color: colors.text, fontFamily: fontMono }}>{activity.qso_count ?? 0}</b> QSO en total en esta actividad
        </p>
      )}

      {activity?.qsl_enabled && (
        <Link to={`/directo/${activityId}/qsl`} style={{ ...s.btnPrimary, textDecoration: "none", display: "inline-flex", marginTop: 4, marginBottom: 6 }}>
          Descargar QSL →
        </Link>
      )}

      {activity && (activeBands.length === 0 || activeModes.length === 0) ? (
        <div style={{ ...s.errorBox, marginTop: 16 }}>Esta actividad todavía no tiene bandas y modos configurados.</div>
      ) : (
        <>
        <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 2fr) minmax(260px, 1fr)", gap: 18, marginTop: 22, alignItems: "start" }}>
          <div style={{ overflowX: "auto", border: `1px solid ${colors.line}`, borderRadius: 10, background: colors.panel, padding: 10 }}>
            <div style={{ display: "grid", gridTemplateColumns: `80px repeat(${activeModes.length}, minmax(80px, 1fr))`, gap: 8, minWidth: 80 + activeModes.length * 80 }}>
              <div style={{ position: "sticky", top: 0, zIndex: 3, background: colors.panel }} />
              {activeModes.map((m) => (
                <div key={m} style={{
                  textAlign: "center", fontSize: 13, fontWeight: 700, color: colors.textDim, padding: "8px 4px",
                  position: "sticky", top: 0, zIndex: 3, background: colors.panel,
                }}>{m}</div>
              ))}
              {activeBands.map((band) => (
                <React.Fragment key={band.id}>
                  <div style={{ display: "flex", alignItems: "center", fontSize: 13, fontWeight: 700, color: colors.text, padding: "4px 6px" }}>{band.label}</div>
                  {activeModes.map((mode) => {
                    const cell = cellFor(band.id, mode);
                    if (isBandModeBlocked(band.id, mode)) {
                      return (
                        <div key={mode} title={`${mode} no está permitido en ${band.label}`} style={{
                          minHeight: 68, borderRadius: 6, border: "1px dashed #2a3542",
                          background: "repeating-linear-gradient(45deg, #12161c, #12161c 8px, #171c23 8px, #171c23 16px)",
                          opacity: 0.5,
                        }} />
                      );
                    }
                    return cell ? (
                      <div key={mode} style={{
                        minHeight: 68, borderRadius: 6, border: `1px solid ${colors.redBorder}`, background: colors.redBg,
                        padding: "6px 8px", display: "flex", flexDirection: "column", justifyContent: "center", gap: 2,
                      }}>
                        <div style={{ fontFamily: fontMono, fontSize: 13, fontWeight: 700, color: "#f4d9d9" }}>🔒 {cell.callsign}</div>
                        <div style={{ fontFamily: fontMono, fontSize: 11.5, color: "#c98f8f" }}>{cell.frequency ? `${formatFrequencyDisplay(cell.frequency)} MHz` : ""}</div>
                      </div>
                    ) : (
                      <div key={mode} style={{ minHeight: 68, borderRadius: 6, border: "1px solid #16311f", background: "#0e1a14" }} />
                    );
                  })}
                </React.Fragment>
              ))}
            </div>
          </div>

          <div style={{ border: `1px solid ${colors.line}`, borderRadius: 10, background: colors.panel, padding: 16 }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", color: colors.textFaint, marginBottom: 12 }}>
              ÚLTIMOS {MAX_RECENT} CONTACTOS
            </div>
            {recentQsos.length === 0 && (
              <p style={{ fontSize: 12.5, color: colors.textFaint, margin: 0 }}>Todavía no hay contactos registrados.</p>
            )}
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {recentQsos.map((q, i) => (
                <div key={`${q.callsign}-${q.logged_at}-${i}`} style={{
                  display: "flex", justifyContent: "space-between", alignItems: "center",
                  borderBottom: i < recentQsos.length - 1 ? `1px solid ${colors.lineStrong}` : "none",
                  paddingBottom: 8,
                }}>
                  <div>
                    <div style={{ fontFamily: fontMono, fontSize: 13, fontWeight: 700, color: colors.text }}>{q.callsign}</div>
                    <div style={{ fontSize: 11, color: colors.textFaint }}>{q.band} · {q.mode} · por {q.operator_callsign}</div>
                  </div>
                  <div style={{ fontFamily: fontMono, fontSize: 11.5, color: colors.textDim }}>{formatUtcTime(q.logged_at)}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div style={{ border: `1px solid ${colors.line}`, borderRadius: 10, background: colors.panel, padding: 16, marginTop: 16 }}>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", color: colors.textFaint, marginBottom: 4 }}>
            ENTIDADES DXCC ({entities.length})
          </div>
          <p style={{ fontSize: 11, color: colors.textFaint, marginTop: 0, marginBottom: 12 }}>
            La referencia real del hobby para contar "países trabajados" (mantenida por la ARRL) — un mismo país
            puede tener varias entidades distintas (p. ej. Canarias cuenta aparte de la España peninsular, aunque
            compartan bandera).
          </p>
          {entities.length === 0 && (
            <p style={{ fontSize: 12.5, color: colors.textFaint, margin: 0 }}>Todavía no hay entidades DXCC registradas.</p>
          )}
          <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
            {entities.map((e) => (
              <div key={e.entity} style={{
                display: "flex", alignItems: "center", gap: 8, background: colors.panelRaised,
                border: `1px solid ${colors.lineStrong}`, borderRadius: 6, padding: "6px 12px",
              }}>
                <img src={flagImgUrl(e.isoCode)} alt="" width={22} style={{ borderRadius: 2, display: "block" }} />
                <span style={{ fontSize: 13, color: colors.text }}>{e.entity}</span>
                <span style={{ fontFamily: fontMono, fontSize: 12, color: colors.textFaint }}>({e.count})</span>
              </div>
            ))}
          </div>
        </div>
        </>
      )}

      <p style={{ marginTop: 18, fontSize: 12, color: colors.textFaint }}>
        Esta vista se actualiza automáticamente y no requiere iniciar sesión. Solo se muestran los
        últimos {MAX_RECENT} contactos de esta actividad — el log completo con todos los detalles
        (informes, exchange, etc.) sigue siendo visible solo para operadores y organizadores.
      </p>
    </div>
  );
}
